import { type FC } from 'react';

interface LoadingSkeletonProps {
  variant?: 'card' | 'table' | 'list' | 'detail' | 'grid';
  rows?: number;
  cols?: number;
  className?: string;
}

const SkeletonBar: FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`animate-pulse rounded bg-background-300/60 ${className}`} />
);

export const LoadingSkeleton: FC<LoadingSkeletonProps> = ({
  variant = 'card',
  rows = 5,
  cols = 3,
  className = '',
}) => {
  if (variant === 'table') {
    return (
      <div className={`space-y-3 ${className}`} role="status" aria-label="Cargando datos">
        <div className="flex items-center gap-3 px-4 py-3 bg-background-50 rounded-lg border border-background-200/70">
          {Array.from({ length: cols }).map((_, ci) => (
            <SkeletonBar key={ci} className="h-4 flex-1" />
          ))}
        </div>
        {Array.from({ length: rows }).map((_, ri) => (
          <div key={ri} className="flex items-center gap-3 px-4 py-3 bg-background-50 rounded-lg border border-background-200/60">
            <SkeletonBar className="h-9 w-9 rounded-full shrink-0" />
            {Array.from({ length: cols - 1 }).map((_, ci) => (
              <SkeletonBar key={ci} className="h-4 flex-1" />
            ))}
            <SkeletonBar className="h-8 w-20 rounded shrink-0" />
          </div>
        ))}
        <span className="sr-only">Cargando...</span>
      </div>
    );
  }

  if (variant === 'list') {
    return (
      <div className={`space-y-3 ${className}`} role="status" aria-label="Cargando datos">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className="flex items-center gap-4 p-4 bg-background-50 rounded-xl border border-background-200/60">
            <SkeletonBar className="h-12 w-12 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <SkeletonBar className="h-4 w-3/4" />
              <SkeletonBar className="h-3 w-1/2" />
            </div>
            <SkeletonBar className="h-8 w-8 rounded shrink-0" />
          </div>
        ))}
        <span className="sr-only">Cargando...</span>
      </div>
    );
  }

  if (variant === 'detail') {
    return (
      <div className={`space-y-5 ${className}`} role="status" aria-label="Cargando datos">
        <SkeletonBar className="h-8 w-2/3" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="space-y-2">
              <SkeletonBar className="h-3 w-1/3" />
              <SkeletonBar className="h-5 w-full" />
            </div>
          ))}
        </div>
        <SkeletonBar className="h-40 w-full rounded-xl" />
        <span className="sr-only">Cargando...</span>
      </div>
    );
  }

  if (variant === 'grid') {
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${className}`} role="status" aria-label="Cargando datos">
        {Array.from({ length: rows * cols }).map((_, i) => (
          <div key={i} className="bg-background-50 rounded-xl border border-background-200/60 p-5 space-y-3">
            <div className="flex items-center gap-3">
              <SkeletonBar className="h-10 w-10 rounded-lg shrink-0" />
              <div className="flex-1 space-y-1.5">
                <SkeletonBar className="h-4 w-3/4" />
                <SkeletonBar className="h-3 w-1/2" />
              </div>
            </div>
            <SkeletonBar className="h-3 w-full" />
            <SkeletonBar className="h-3 w-5/6" />
            <div className="flex gap-3 pt-2">
              <SkeletonBar className="h-8 flex-1 rounded" />
              <SkeletonBar className="h-8 w-20 rounded" />
            </div>
          </div>
        ))}
        <span className="sr-only">Cargando...</span>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`} role="status" aria-label="Cargando datos">
      <div className="flex items-center gap-3 mb-6">
        <SkeletonBar className="h-10 w-10 rounded-lg shrink-0" />
        <SkeletonBar className="h-5 w-48" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="bg-background-50 rounded-xl border border-background-200/60 p-4 space-y-2">
            <SkeletonBar className="h-3 w-1/2" />
            <SkeletonBar className="h-7 w-3/4" />
            <SkeletonBar className="h-3 w-1/3" />
          </div>
        ))}
      </div>
      <SkeletonBar className="h-64 w-full rounded-xl" />
      <span className="sr-only">Cargando...</span>
    </div>
  );
};

export default LoadingSkeleton;